"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  TrendingUp,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Loader2,
  RefreshCw,
  Info,
  CheckCircle,
  Percent,
  Clock,
  Shield,
  Zap,
  Lock,
  X,
  Timer,
  BarChart3,
  Search,
  SlidersHorizontal,
  LayoutGrid,
  List,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Rocket,
  Calendar,
  Activity,
  TrendingDown,
} from "lucide-react";
import { useWallet, EVMWallet } from "@crossmint/client-sdk-react-ui";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import toast, { Toaster } from "react-hot-toast";
import { getBalance } from "../services/authapi";
import {
  fetchYields,
  enterYield,
  exitYield,
  getYieldBalance,
  discoverPositions,
  YieldOpportunity,
  YieldPosition,
} from "../utils/yield-api";

const formatTvl = (tvl: string) => {
  const num = parseFloat(tvl);
  if (isNaN(num) || num === 0) return null;
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
};

const sanitizeText = (text: string) =>
  text
    .replace(/\bUSDC\b/g, "USD")
    .replace(/\bBase\b/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const formatCooldown = (seconds: number) => {
  if (!seconds) return null;
  if (seconds >= 86400) return `${Math.round(seconds / 86400)}d cooldown`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)}h cooldown`;
  return `${Math.round(seconds / 60)}m cooldown`;
};

const formatTimeAgo = (dateStr: string) => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatDuration = (dateStr: string) => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""}`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""}`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? "s" : ""}`;
};

const ITEMS_PER_PAGE = 12;

export default function EarnPage() {
  const { wallet } = useWallet();
  const user = useSelector((state: RootState) => state.user) as any;
  const walletAddress = (user?.smartWallet || wallet?.address || "").toLowerCase();
  const walletAddressWithoutLowerCase = user?.smartWallet || wallet?.address || "";

  const [yields, setYields] = useState<YieldOpportunity[]>([]);
  const [activePositions, setActivePositions] = useState<YieldPosition[]>([]);
  const [positionBalances, setPositionBalances] = useState<
    Record<string, string>
  >({});
  const [selectedYield, setSelectedYield] = useState<YieldOpportunity | null>(
    null,
  );
  const [depositAmount, setDepositAmount] = useState("");
  const [walletBalance, setWalletBalance] = useState("0");
  const [loadingYields, setLoadingYields] = useState(true);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [showDepositModal, setShowDepositModal] = useState(false);

  // Search, filter, view, pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [minApy, setMinApy] = useState("");
  const [maxApy, setMaxApy] = useState("");
  const [minTvl, setMinTvl] = useState("");
  const [maxTvl, setMaxTvl] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [sortBy, setSortBy] = useState<
    "apy-desc" | "apy-asc" | "tvl-desc" | "tvl-asc"
  >("apy-desc");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [positionsExpanded, setPositionsExpanded] = useState(true);

  const loadYields = useCallback(async () => {
    setLoadingYields(true);
    try {
      const data = await fetchYields();
      setYields(data);
    } catch (err: any) {
      console.error("Failed to fetch yields:", err);
      toast.error("Failed to load yield opportunities");
    } finally {
      setLoadingYields(false);
    }
  }, []);

  // Every vault we know about. Positions are discovered by reading the live
  // balance of each one, so this has to span the whole catalogue.
  const catalogueIds = useMemo(() => yields.map((y) => y.id), [yields]);

  const loadPositions = useCallback(async () => {
    if (!walletAddress || catalogueIds.length === 0) return;
    setLoadingPositions(true);
    try {
      // Balances are the source of truth, not the API's action history: that
      // log only covers deposits made through this app and gets pruned, so
      // relying on it hid real positions and left them with no Withdraw button.
      const positions = await discoverPositions(walletAddress, catalogueIds);
      setActivePositions(positions);
      setPositionBalances(
        Object.fromEntries(positions.map((p) => [p.yieldId, p.balance])),
      );
    } catch (err: any) {
      console.error("Failed to fetch positions:", err);
    } finally {
      setLoadingPositions(false);
    }
  }, [walletAddress, catalogueIds]);

  const loadWalletBalance = useCallback(async () => {
    if (!walletAddress) return;
    setLoadingBalance(true);
    try {
      const bal = await getBalance(walletAddressWithoutLowerCase);
      setWalletBalance(bal);
    } catch {
      setWalletBalance("0");
    } finally {
      setLoadingBalance(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    loadYields();
  }, [loadYields]);

  useEffect(() => {
    if (walletAddress) {
      loadPositions();
      loadWalletBalance();
    }
  }, [walletAddress, loadPositions, loadWalletBalance]);

  // Extract unique providers from vault yields for filter dropdown
  const availableProviders = useMemo(() => {
    const vaults = yields.filter((y) => y.type.toLowerCase() === "vault");
    const providers = [...new Set(vaults.map((y) => y.provider))].sort();
    return providers;
  }, [yields]);

  // Compute APY and TVL bounds from vault data for range sliders
  const apyBounds = useMemo(() => {
    const vaults = yields.filter((y) => y.type.toLowerCase() === "vault");
    if (vaults.length === 0) return { min: 0, max: 50 };
    const apys = vaults.map((y) => y.apy * 100);
    return { min: 0, max: Math.ceil(Math.max(...apys)) };
  }, [yields]);

  const tvlBounds = useMemo(() => {
    const vaults = yields.filter((y) => y.type.toLowerCase() === "vault");
    if (vaults.length === 0) return { min: 0, max: 1000000000 };
    const tvls = vaults
      .map((y) => parseFloat(y.tvlUsd))
      .filter((v) => !isNaN(v));
    return { min: 0, max: Math.ceil(Math.max(...tvls, 1)) };
  }, [yields]);

  // Filtered & paginated yields
  const filteredYields = useMemo(() => {
    // Only show vault type
    let result = yields.filter((y) => y.type.toLowerCase() === "vault");

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (y) =>
          y.name.toLowerCase().includes(q) ||
          y.provider.toLowerCase().includes(q) ||
          y.description?.toLowerCase().includes(q),
      );
    }

    // Provider filter
    if (selectedProvider) {
      result = result.filter((y) => y.provider === selectedProvider);
    }

    // APY filters
    if (minApy !== "") {
      const min = parseFloat(minApy) / 100;
      result = result.filter((y) => y.apy >= min);
    }
    if (maxApy !== "") {
      const max = parseFloat(maxApy) / 100;
      result = result.filter((y) => y.apy <= max);
    }

    // TVL filters
    if (minTvl !== "") {
      const min = parseFloat(minTvl);
      result = result.filter((y) => parseFloat(y.tvlUsd) >= min);
    }
    if (maxTvl !== "") {
      const max = parseFloat(maxTvl);
      result = result.filter((y) => parseFloat(y.tvlUsd) <= max);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "apy-desc":
          return b.apy - a.apy;
        case "apy-asc":
          return a.apy - b.apy;
        case "tvl-desc":
          return parseFloat(b.tvlUsd) - parseFloat(a.tvlUsd);
        case "tvl-asc":
          return parseFloat(a.tvlUsd) - parseFloat(b.tvlUsd);
        default:
          return 0;
      }
    });

    return result;
  }, [
    yields,
    searchQuery,
    selectedProvider,
    minApy,
    maxApy,
    minTvl,
    maxTvl,
    sortBy,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredYields.length / ITEMS_PER_PAGE),
  );
  const paginatedYields = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredYields.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredYields, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedProvider, minApy, maxApy, minTvl, maxTvl, sortBy]);

  const activeFilterCount =
    [minApy, maxApy, minTvl, maxTvl].filter((v) => v !== "").length +
    (selectedProvider ? 1 : 0) +
    (sortBy !== "apy-desc" ? 1 : 0);

  const clearFilters = () => {
    setMinApy("");
    setMaxApy("");
    setMinTvl("");
    setMaxTvl("");
    setSelectedProvider("");
    setSortBy("apy-desc");
    setSearchQuery("");
  };

  // Show anything worth more than dust. The old $1 floor silently hid real
  // (small) positions along with their Withdraw button.
  const visiblePositions = activePositions.filter((p) => {
    const bal = parseFloat(positionBalances[p.yieldId] || p.balance || "0");
    return bal >= 0.01;
  });

  const executeTransactions = async (transactions: any[]) => {
    if (!wallet) throw new Error("Wallet not connected");

    const evmWallet = EVMWallet.from(wallet);

    const sorted = [...transactions].sort(
      (a, b) => (a.stepIndex || 0) - (b.stepIndex || 0),
    );

    for (const tx of sorted) {
      const unsigned =
        typeof tx.unsignedTransaction === "string"
          ? JSON.parse(tx.unsignedTransaction)
          : tx.unsignedTransaction;

      await evmWallet.sendTransaction({
        to: unsigned.to,
        data: unsigned.data as `0x${string}`,
        value: unsigned.value ? BigInt(unsigned.value) : undefined,
      });
    }
  };

  const handleDeposit = async () => {
    if (!selectedYield || !depositAmount || !walletAddress) return;

    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Minimum deposit is $1.00");
      return;
    }

    const balance = parseFloat(walletBalance);
    if (amount > balance) {
      toast.error("Insufficient balance");
      return;
    }

    setDepositing(true);
    try {
      const action = await enterYield(
        selectedYield.id,
        walletAddress,
        depositAmount,
      );

      if (action.transactions?.length) {
        await executeTransactions(action.transactions);
      }

      toast.success(
        `Successfully deposited $${depositAmount} USD into ${sanitizeText(selectedYield.name)}`,
      );
      setDepositAmount("");
      setShowDepositModal(false);
      setSelectedYield(null);
      await loadPositions();
      await loadWalletBalance();
    } catch (err: any) {
      console.error("Deposit failed:", err);
      toast.error(err.message || "Deposit failed. Please try again.");
    } finally {
      setDepositing(false);
    }
  };

  const handleWithdraw = async (position: YieldPosition) => {
    if (!walletAddress) return;

    setWithdrawingId(position.yieldId);
    try {
      // Re-read the balance immediately before exiting so the amount matches
      // what the vault will actually release, then fall back to the value we
      // discovered this position with.
      let withdrawAmount = "0";
      try {
        withdrawAmount = await getYieldBalance(position.yieldId, walletAddress);
      } catch {
        withdrawAmount = "0";
      }
      if (parseFloat(withdrawAmount) === 0) {
        withdrawAmount =
          positionBalances[position.yieldId] || position.balance || "0";
      }

      if (parseFloat(withdrawAmount) === 0) {
        toast.error("No balance to withdraw");
        return;
      }

      const action = await exitYield(
        position.yieldId,
        walletAddress,
        withdrawAmount,
      );

      if (action.transactions?.length) {
        await executeTransactions(action.transactions);
      }

      toast.success("Withdrawal successful!");
      await loadPositions();
      await loadWalletBalance();
    } catch (err: any) {
      console.error("Withdrawal failed:", err);
      toast.error(err.message || "Withdrawal failed. Please try again.");
    } finally {
      setWithdrawingId(null);
    }
  };

  const openDepositModal = (yieldOpp: YieldOpportunity) => {
    setSelectedYield(yieldOpp);
    setDepositAmount("");
    setShowDepositModal(true);
    loadWalletBalance();
  };

  const closeDepositModal = () => {
    setShowDepositModal(false);
    setSelectedYield(null);
    setDepositAmount("");
  };

  const handleMaxClick = () => {
    setDepositAmount(walletBalance);
  };

  const depositExceedsBalance =
    depositAmount !== "" &&
    parseFloat(depositAmount) > parseFloat(walletBalance);

  // --- Render helpers ---

  const renderYieldCard = (y: YieldOpportunity) => {
    const apyPercent = y.apy * 100;
    const tvl = formatTvl(y.tvlUsd);
    const cooldown = formatCooldown(y.cooldownPeriod);

    if (viewMode === "list") {
      return (
        <div
          key={y.id}
          className="bg-gradient-to-r from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-4 hover:border-[#61CE70]/30 transition-all group"
        >
          <div className="flex items-center gap-4">
            {/* Logo with US flag */}
            <div className="relative shrink-0">
              <img
                src="/Flag_of_the_United_States.png"
                alt=""
                className="w-9 h-9 rounded-full ring-2 ring-gray-700/50"
              />
            </div>

            {/* Name & provider */}
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-white group-hover:text-[#61CE70] transition-colors truncate">
                {sanitizeText(y.name)}
              </h3>
              <p className="text-xs text-gray-500 capitalize">
                {y.provider} &middot; Vault
              </p>
            </div>

            {/* Stats row */}
            <div className="hidden sm:flex items-center gap-4 text-sm shrink-0">
              {apyPercent > 0 && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-[#61CE70]/15 rounded-lg">
                  <Percent className="w-3 h-3 text-[#61CE70]" />
                  <span className="text-[#61CE70] font-bold">
                    {apyPercent.toFixed(2)}%
                  </span>
                </div>
              )}
              {tvl && (
                <span className="text-gray-400 flex items-center gap-1">
                  <BarChart3 className="w-3.5 h-3.5" />
                  {tvl}
                </span>
              )}
              {cooldown && (
                <span className="text-gray-400 flex items-center gap-1">
                  <Timer className="w-3.5 h-3.5" />
                  {cooldown}
                </span>
              )}
            </div>

            {/* Deposit button */}
            <button
              onClick={() => openDepositModal(y)}
              disabled={!walletAddress}
              className="flex items-center gap-2 px-4 py-2 bg-[#61CE70]/15 text-[#61CE70] rounded-lg hover:bg-[#61CE70]/25 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <ArrowDownToLine className="w-4 h-4" />
              Deposit
            </button>
          </div>

          {/* Mobile stats */}
          <div className="flex sm:hidden flex-wrap items-center gap-2 mt-3 text-xs">
            {apyPercent > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#61CE70]/15 rounded text-[#61CE70] font-bold">
                {apyPercent.toFixed(2)}% APY
              </span>
            )}
            {tvl && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-800/60 rounded text-gray-400">
                <BarChart3 className="w-3 h-3" />
                {tvl}
              </span>
            )}
          </div>
        </div>
      );
    }

    // Grid view (default)
    return (
      <div
        key={y.id}
        className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-5 hover:border-[#61CE70]/30 transition-all group flex flex-col"
      >
        <div className="space-y-3 flex-1">
          {/* Header: Name + APY */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative shrink-0">
                <img
                  src="/Flag_of_the_United_States.png"
                  alt=""
                  className="w-9 h-9 rounded-full ring-2 ring-gray-700/50"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-white group-hover:text-[#61CE70] transition-colors truncate">
                  {sanitizeText(y.name)}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 capitalize">
                  {y.provider} &middot; Vault
                </p>
              </div>
            </div>
            {apyPercent > 0 && (
              <div className="flex items-center gap-1 px-2.5 py-1 bg-[#61CE70]/15 rounded-lg shrink-0">
                <Percent className="w-3 h-3 text-[#61CE70]" />
                <span className="text-[#61CE70] font-bold text-sm">
                  {apyPercent.toFixed(2)}%
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {y.description && (
            <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
              {sanitizeText(y.description)}
            </p>
          )}

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-800/60 rounded text-gray-400">
              USD
            </span>
            {tvl && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-800/60 rounded text-gray-400">
                <BarChart3 className="w-3 h-3" />
                TVL {tvl}
              </span>
            )}
            {cooldown && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-800/60 rounded text-gray-400">
                <Timer className="w-3 h-3" />
                {cooldown}
              </span>
            )}
          </div>
        </div>

        {/* Deposit button */}
        <button
          onClick={() => openDepositModal(y)}
          disabled={!walletAddress}
          className="w-full flex items-center justify-center gap-2 py-2.5 mt-3 bg-[#61CE70]/15 text-[#61CE70] rounded-lg hover:bg-[#61CE70]/25 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowDownToLine className="w-4 h-4" />
          Deposit
        </button>
      </div>
    );
  };

  return (
    <div className={`relative max-w-5xl mx-auto space-y-6 sm:space-y-8`}>
      <Toaster position="top-right" />

      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#61CE70]/15 rounded-xl">
              <TrendingUp className="w-6 h-6 text-[#61CE70]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Earn Yield
              </h1>
              <p className="text-gray-400 text-sm">
                Put your USD to work and earn passive income
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              loadYields();
              if (walletAddress) loadPositions();
            }}
            className="p-2 bg-gray-800/50 rounded-lg hover:bg-gray-700/50 transition-colors"
            title="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-400 ${loadingYields ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-[#61CE70]/10 to-[#50B060]/5 border border-[#61CE70]/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-[#61CE70] shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p>
            Earn yield on your USD by depositing into curated opportunities
            powered by{" "}
            <span className="text-[#61CE70] font-medium">Yield.xyz</span>. Your
            funds remain accessible and can be withdrawn at any time.
          </p>
        </div>
      </div>

      {/* Active Positions - Collapsible */}
      {visiblePositions.length > 0 && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl overflow-hidden">
          {/* Collapsible Header */}
          <button
            onClick={() => setPositionsExpanded(!positionsExpanded)}
            className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-gray-800/20 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <CheckCircle className="w-5 h-5 text-[#61CE70]" />
              <h2 className="text-lg font-semibold text-white">
                Your Active Positions
              </h2>
              <span className="px-2 py-0.5 bg-[#61CE70]/15 text-[#61CE70] text-xs font-bold rounded-full">
                {visiblePositions.length}
              </span>
            </div>
            {positionsExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {/* Collapsible Content */}
          {positionsExpanded && (
            <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3">
              {visiblePositions.map((pos) => {
                const yieldInfo = yields.find((y) => y.id === pos.yieldId);
                const balNum = parseFloat(
                  positionBalances[pos.yieldId] || pos.balance || "0",
                );
                const depositedAmt = parseFloat(pos.depositedAmount || "0");
                // Only claim a gain when we know what was actually put in —
                // positions opened outside this app have no deposit record.
                const earned = depositedAmt > 0 ? balNum - depositedAmt : 0;
                const apyPercent = yieldInfo ? yieldInfo.apy * 100 : 0;
                const expectedYearly = balNum * (yieldInfo?.apy || 0);
                const expectedMonthly = expectedYearly / 12;
                const expectedDaily = expectedYearly / 365;
                const roiPercent =
                  depositedAmt > 0 ? (earned / depositedAmt) * 100 : 0;
                const cooldown = yieldInfo
                  ? formatCooldown(yieldInfo.cooldownPeriod)
                  : null;

                return (
                  <div
                    key={pos.id}
                    className="bg-gradient-to-br from-gray-800/30 to-gray-800/10 border border-gray-700/30 rounded-2xl overflow-hidden"
                  >
                    {/* Header */}
                    <div className="px-5 pt-5 pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="relative shrink-0">
                            <img
                              src="/Flag_of_the_United_States.png"
                              alt=""
                              className="w-9 h-9 rounded-full ring-2 ring-gray-700/50"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-white truncate text-base">
                              {sanitizeText(yieldInfo?.name || pos.yieldId)}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {yieldInfo && (
                                <span className="text-xs text-gray-500 capitalize">
                                  {yieldInfo.provider} &middot; {yieldInfo.type}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {apyPercent > 0 && (
                            <span className="px-2.5 py-1 bg-[#61CE70]/15 text-[#61CE70] text-xs font-bold rounded-lg">
                              {apyPercent.toFixed(2)}% APY
                            </span>
                          )}
                          {pos.openedAt && (
                            <span className="text-[10px] text-gray-500">
                              {formatTimeAgo(pos.openedAt)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Large balance display */}
                      <div className="mt-4 flex items-end justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                            Current Value
                          </p>
                          <p className="text-2xl font-bold text-white">
                            ${balNum.toFixed(2)}
                          </p>
                        </div>
                        {earned !== 0 && (
                          <div className="flex items-center gap-1.5 mb-1">
                            {earned > 0 ? (
                              <TrendingUp className="w-4 h-4 text-[#61CE70]" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-400" />
                            )}
                            <span
                              className={`text-sm font-semibold ${
                                earned > 0 ? "text-[#61CE70]" : "text-red-400"
                              }`}
                            >
                              {earned > 0 ? "+" : ""}$
                              {Math.abs(earned).toFixed(4)}
                            </span>
                            {roiPercent !== 0 && (
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  roiPercent > 0
                                    ? "bg-[#61CE70]/10 text-[#61CE70]"
                                    : "bg-red-500/10 text-red-400"
                                }`}
                              >
                                {roiPercent > 0 ? "+" : ""}
                                {roiPercent.toFixed(2)}%
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ROI Progress bar */}
                      {depositedAmt > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                            <span>Deposited ${depositedAmt.toFixed(2)}</span>
                            <span>
                              Growth {roiPercent >= 0 ? "+" : ""}
                              {roiPercent.toFixed(3)}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-700/40 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                roiPercent >= 0
                                  ? "bg-gradient-to-r from-[#61CE70]/70 to-[#61CE70]"
                                  : "bg-gradient-to-r from-red-500/70 to-red-400"
                              }`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.max(1, Math.abs(roiPercent) * 20 + 2),
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-gray-700/20 bg-black/10">
                      <div className="px-4 py-3 border-r border-b sm:border-b-0 border-gray-700/20">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Calendar className="w-3 h-3 text-gray-500" />
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">
                            Deposited On
                          </p>
                        </div>
                        <p className="text-xs text-gray-300 font-medium">
                          {pos.openedAt
                            ? new Date(pos.openedAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : "—"}
                        </p>
                      </div>
                      <div className="px-4 py-3 sm:border-r border-b sm:border-b-0 border-gray-700/20">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Activity className="w-3 h-3 text-gray-500" />
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">
                            Time Active
                          </p>
                        </div>
                        <p className="text-xs text-gray-300 font-medium">
                          {pos.openedAt ? formatDuration(pos.openedAt) : "—"}
                        </p>
                      </div>
                      <div className="px-4 py-3 border-r border-gray-700/20">
                        <div className="flex items-center gap-1.5 mb-1">
                          <DollarSign className="w-3 h-3 text-gray-500" />
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">
                            Est. Daily / Monthly
                          </p>
                        </div>
                        <p className="text-xs text-gray-300 font-medium">
                          {expectedDaily > 0
                            ? `$${expectedDaily.toFixed(4)} / $${expectedMonthly.toFixed(2)}`
                            : "—"}
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <TrendingUp className="w-3 h-3 text-gray-500" />
                          <p className="text-[10px] uppercase tracking-wider text-gray-500">
                            Est. Yearly
                          </p>
                        </div>
                        <p className="text-xs text-[#61CE70] font-semibold">
                          {expectedYearly > 0
                            ? `$${expectedYearly.toFixed(2)}/yr`
                            : "—"}
                        </p>
                      </div>
                    </div>

                    {/* Footer: cooldown info + withdraw */}
                    <div className="flex items-center justify-between gap-3 border-t border-gray-700/20 px-5 py-3">
                      {cooldown ? (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Timer className="w-3.5 h-3.5" />
                          {cooldown}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Zap className="w-3.5 h-3.5" />
                          Instant withdrawal
                        </span>
                      )}
                      <button
                        onClick={() => handleWithdraw(pos)}
                        disabled={withdrawingId === pos.yieldId}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500/20 to-red-600/15 text-red-400 border border-red-500/25 rounded-xl hover:from-red-500/30 hover:to-red-600/25 transition-all disabled:opacity-50 text-sm font-semibold"
                      >
                        {withdrawingId === pos.yieldId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowUpFromLine className="w-4 h-4" />
                        )}
                        Withdraw ${balNum.toFixed(2)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Available Yields */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#61CE70]" />
            Available Vaults
            {!loadingYields && yields.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({filteredYields.length} vaults)
              </span>
            )}
          </h2>
        </div>

        {/* Search & Controls Bar */}
        {!loadingYields && yields.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vaults by name or provider..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#61CE70]/50 transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                {/* Filter toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors border ${
                    showFilters || activeFilterCount > 0
                      ? "bg-[#61CE70]/15 border-[#61CE70]/30 text-[#61CE70]"
                      : "bg-gray-800/50 border-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700/50"
                  }`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="px-1.5 py-0.5 bg-[#61CE70] text-white text-[10px] font-bold rounded-full leading-none">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* View toggle */}
                <div className="flex items-center bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2.5 transition-colors ${
                      viewMode === "grid"
                        ? "bg-[#61CE70]/15 text-[#61CE70]"
                        : "text-gray-500 hover:text-white"
                    }`}
                    title="Grid view"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2.5 transition-colors ${
                      viewMode === "list"
                        ? "bg-[#61CE70]/15 text-[#61CE70]"
                        : "text-gray-500 hover:text-white"
                    }`}
                    title="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="bg-gradient-to-br from-gray-800/30 to-gray-800/10 border border-gray-700/40 rounded-2xl p-4 sm:p-5 space-y-5">
                {/* Row 1: Sort + Provider */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Sort By */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <TrendingUp className="w-3 h-3" />
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) =>
                        setSortBy(e.target.value as typeof sortBy)
                      }
                      className="w-full px-3 py-2.5 bg-gray-800/60 border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:border-[#61CE70]/50 appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                      }}
                    >
                      <option value="apy-desc">APY: High to Low</option>
                      <option value="apy-asc">APY: Low to High</option>
                      <option value="tvl-desc">TVL: High to Low</option>
                      <option value="tvl-asc">TVL: Low to High</option>
                    </select>
                  </div>

                  {/* Provider */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <Shield className="w-3 h-3" />
                      Provider
                    </label>
                    <select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      className="w-full px-3 py-2.5 bg-gray-800/60 border border-gray-700/50 rounded-xl text-white text-sm focus:outline-none focus:border-[#61CE70]/50 appearance-none cursor-pointer capitalize"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                      }}
                    >
                      <option value="">All Providers</option>
                      {availableProviders.map((p) => (
                        <option key={p} value={p} className="capitalize">
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: APY Range Slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <Percent className="w-3 h-3" />
                      APY Range
                    </label>
                    <span className="text-xs text-[#61CE70] font-semibold tabular-nums">
                      {minApy || "0"}% — {maxApy || apyBounds.max}%
                    </span>
                  </div>
                  <div className="relative h-8 flex items-center">
                    {/* Track background */}
                    <div className="absolute inset-x-0 h-1.5 bg-gray-700/60 rounded-full" />
                    {/* Active track */}
                    <div
                      className="absolute h-1.5 bg-[#61CE70] rounded-full"
                      style={{
                        left: `${((parseFloat(minApy) || 0) / apyBounds.max) * 100}%`,
                        right: `${100 - ((parseFloat(maxApy) || apyBounds.max) / apyBounds.max) * 100}%`,
                      }}
                    />
                    {/* Min slider */}
                    <input
                      type="range"
                      min={0}
                      max={apyBounds.max}
                      step={0.5}
                      value={parseFloat(minApy) || 0}
                      onChange={(e) => {
                        const val = e.target.value;
                        const maxVal = parseFloat(maxApy) || apyBounds.max;
                        if (parseFloat(val) <= maxVal)
                          setMinApy(parseFloat(val) === 0 ? "" : val);
                      }}
                      className="absolute inset-x-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#61CE70] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#61CE70] [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                    {/* Max slider */}
                    <input
                      type="range"
                      min={0}
                      max={apyBounds.max}
                      step={0.5}
                      value={parseFloat(maxApy) || apyBounds.max}
                      onChange={(e) => {
                        const val = e.target.value;
                        const minVal = parseFloat(minApy) || 0;
                        if (parseFloat(val) >= minVal)
                          setMaxApy(
                            parseFloat(val) === apyBounds.max ? "" : val,
                          );
                      }}
                      className="absolute inset-x-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#61CE70] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#61CE70] [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-600">
                    <span>0%</span>
                    <span>{apyBounds.max}%</span>
                  </div>
                </div>

                {/* Row 3: TVL Range Slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <BarChart3 className="w-3 h-3" />
                      Total Value Locked
                    </label>
                    <span className="text-xs text-[#61CE70] font-semibold tabular-nums">
                      {formatTvl(String(parseFloat(minTvl) || 0)) || "$0"} —{" "}
                      {formatTvl(String(parseFloat(maxTvl) || tvlBounds.max)) ||
                        formatTvl(String(tvlBounds.max))}
                    </span>
                  </div>
                  <div className="relative h-8 flex items-center">
                    {/* Track background */}
                    <div className="absolute inset-x-0 h-1.5 bg-gray-700/60 rounded-full" />
                    {/* Active track — use log scale for TVL */}
                    {(() => {
                      const logMax = Math.log10(Math.max(tvlBounds.max, 1));
                      const logMinVal = Math.log10(
                        Math.max(parseFloat(minTvl) || 1, 1),
                      );
                      const logMaxVal = Math.log10(
                        Math.max(parseFloat(maxTvl) || tvlBounds.max, 1),
                      );
                      return (
                        <div
                          className="absolute h-1.5 bg-[#61CE70] rounded-full"
                          style={{
                            left: `${(logMinVal / logMax) * 100}%`,
                            right: `${100 - (logMaxVal / logMax) * 100}%`,
                          }}
                        />
                      );
                    })()}
                    {/* Min slider — log scale via steps */}
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        if (!minTvl) return 0;
                        const logMax = Math.log10(Math.max(tvlBounds.max, 1));
                        const logVal = Math.log10(
                          Math.max(parseFloat(minTvl), 1),
                        );
                        return Math.round((logVal / logMax) * 100);
                      })()}
                      onChange={(e) => {
                        const pct = parseInt(e.target.value);
                        if (pct === 0) {
                          setMinTvl("");
                          return;
                        }
                        const logMax = Math.log10(Math.max(tvlBounds.max, 1));
                        const val = Math.pow(10, (pct / 100) * logMax);
                        const maxVal = parseFloat(maxTvl) || tvlBounds.max;
                        if (val <= maxVal)
                          setMinTvl(Math.round(val).toString());
                      }}
                      className="absolute inset-x-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#61CE70] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#61CE70] [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                    {/* Max slider — log scale */}
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={(() => {
                        if (!maxTvl) return 100;
                        const logMax = Math.log10(Math.max(tvlBounds.max, 1));
                        const logVal = Math.log10(
                          Math.max(parseFloat(maxTvl), 1),
                        );
                        return Math.round((logVal / logMax) * 100);
                      })()}
                      onChange={(e) => {
                        const pct = parseInt(e.target.value);
                        if (pct === 100) {
                          setMaxTvl("");
                          return;
                        }
                        const logMax = Math.log10(Math.max(tvlBounds.max, 1));
                        const val = Math.pow(10, (pct / 100) * logMax);
                        const minVal = parseFloat(minTvl) || 0;
                        if (val >= minVal)
                          setMaxTvl(Math.round(val).toString());
                      }}
                      className="absolute inset-x-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#61CE70] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#61CE70] [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-600">
                    <span>$0</span>
                    <span>{formatTvl(String(tvlBounds.max))}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Active Filter Breadcrumbs */}
            {(activeFilterCount > 0 || searchQuery) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">
                  Active:
                </span>

                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    Search: &ldquo;
                    {searchQuery.length > 15
                      ? searchQuery.slice(0, 15) + "..."
                      : searchQuery}
                    &rdquo;
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {selectedProvider && (
                  <button
                    onClick={() => setSelectedProvider("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group capitalize"
                  >
                    Provider: {selectedProvider}
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {sortBy !== "apy-desc" && (
                  <button
                    onClick={() => setSortBy("apy-desc")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    Sort:{" "}
                    {sortBy === "apy-asc"
                      ? "APY Low→High"
                      : sortBy === "tvl-desc"
                        ? "TVL High→Low"
                        : "TVL Low→High"}
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {minApy && (
                  <button
                    onClick={() => setMinApy("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    APY &ge; {minApy}%
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {maxApy && (
                  <button
                    onClick={() => setMaxApy("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    APY &le; {maxApy}%
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {minTvl && (
                  <button
                    onClick={() => setMinTvl("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    TVL &ge; ${Number(minTvl).toLocaleString()}
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {maxTvl && (
                  <button
                    onClick={() => setMaxTvl("")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800/60 border border-gray-700/40 rounded-lg text-xs text-gray-300 hover:border-red-500/40 hover:text-red-400 transition-colors group"
                  >
                    TVL &le; ${Number(maxTvl).toLocaleString()}
                    <X className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </button>
                )}

                {/* Clear all */}
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-400 hover:text-red-300 transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {loadingYields ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#61CE70]" />
          </div>
        ) : yields.length === 0 ? (
          <div className="space-y-5">
            {/* Coming Soon Hero */}
            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/20 rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#61CE70]/5 via-transparent to-[#61CE70]/5 pointer-events-none" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#61CE70]/15 rounded-full text-[#61CE70] text-xs font-semibold mb-5">
                  <Clock className="w-3.5 h-3.5" />
                  COMING SOON
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Yield Opportunities Are On The Way
                </h3>
                <p className="text-gray-400 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
                  We're finalizing curated yield strategies for your wallet.
                  Soon you'll be able to deposit USD and earn passive income
                  directly from CashTap.
                </p>
              </div>
            </div>

            {/* Preview Cards */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-5 space-y-3">
                <div className="p-2.5 bg-[#61CE70]/10 rounded-lg w-fit">
                  <Zap className="w-5 h-5 text-[#61CE70]" />
                </div>
                <p className="text-white font-medium text-sm">
                  High APY Vaults
                </p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Access top-performing USD vaults with competitive annual
                  percentage yields.
                </p>
              </div>
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-5 space-y-3">
                <div className="p-2.5 bg-[#61CE70]/10 rounded-lg w-fit">
                  <Shield className="w-5 h-5 text-[#61CE70]" />
                </div>
                <p className="text-white font-medium text-sm">
                  Curated & Secure
                </p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Every strategy is vetted by Yield.xyz.
                </p>
              </div>
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-5 space-y-3">
                <div className="p-2.5 bg-[#61CE70]/10 rounded-lg w-fit">
                  <Lock className="w-5 h-5 text-[#61CE70]" />
                </div>
                <p className="text-white font-medium text-sm">No Lock-ups</p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Withdraw your funds at any time. No minimum deposit period or
                  exit penalties.
                </p>
              </div>
            </div>
          </div>
        ) : filteredYields.length === 0 ? (
          // No results after filtering
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-10 text-center space-y-3">
            <Search className="w-10 h-10 text-gray-600 mx-auto" />
            <p className="text-gray-400 text-sm">
              No yield opportunities match your filters.
            </p>
            <button
              onClick={clearFilters}
              className="text-[#61CE70] text-sm font-medium hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <>
            {/* Yield Cards */}
            <div
              className={
                viewMode === "grid"
                  ? "grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                  : "space-y-3"
              }
            >
              {paginatedYields.map(renderYieldCard)}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .reduce<(number | "ellipsis")[]>((acc, page, i, arr) => {
                    if (i > 0 && arr[i - 1] !== page - 1) {
                      acc.push("ellipsis");
                    }
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, i) =>
                    item === "ellipsis" ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="px-2 text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === item
                            ? "bg-[#61CE70] text-white"
                            : "bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700/50"
                        }`}
                      >
                        {item}
                      </button>
                    ),
                  )}

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-xl p-5 sm:p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">How It Works</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="w-8 h-8 rounded-full bg-[#61CE70]/15 flex items-center justify-center text-[#61CE70] font-bold text-sm">
              1
            </div>
            <p className="text-sm text-white font-medium">Choose a Strategy</p>
            <p className="text-xs text-gray-400">
              Browse curated yield opportunities and pick one that suits your
              goals.
            </p>
          </div>
          <div className="space-y-2">
            <div className="w-8 h-8 rounded-full bg-[#61CE70]/15 flex items-center justify-center text-[#61CE70] font-bold text-sm">
              2
            </div>
            <p className="text-sm text-white font-medium">Deposit USD</p>
            <p className="text-xs text-gray-400">
              Enter the amount you'd like to deposit. Your funds start earning
              yield immediately.
            </p>
          </div>
          <div className="space-y-2">
            <div className="w-8 h-8 rounded-full bg-[#61CE70]/15 flex items-center justify-center text-[#61CE70] font-bold text-sm">
              3
            </div>
            <p className="text-sm text-white font-medium">
              Earn & Withdraw Anytime
            </p>
            <p className="text-xs text-gray-400">
              Watch your balance grow and withdraw your funds whenever you want
              - no lock-ups.
            </p>
          </div>
        </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && selectedYield && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0F1419] border border-gray-700/50 rounded-2xl p-6 w-full max-w-md space-y-5 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Deposit into {sanitizeText(selectedYield.name)}
              </h3>
              <button
                onClick={closeDepositModal}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Yield Info */}
            <div className="bg-gray-800/30 rounded-xl p-3 space-y-2">
              {selectedYield.description && (
                <p className="text-xs text-gray-400 leading-relaxed pb-1 border-b border-gray-700/40">
                  {sanitizeText(selectedYield.description)}
                </p>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Provider</span>
                <span className="text-white capitalize">
                  {selectedYield.provider}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Type</span>
                <span className="text-white capitalize">
                  {selectedYield.type}
                </span>
              </div>
              {selectedYield.apy > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">APY</span>
                  <span className="text-[#61CE70] font-semibold">
                    {(selectedYield.apy * 100).toFixed(2)}%
                  </span>
                </div>
              )}
              {formatTvl(selectedYield.tvlUsd) && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Total Value Locked</span>
                  <span className="text-white font-medium">
                    {formatTvl(selectedYield.tvlUsd)}
                  </span>
                </div>
              )}
              {formatCooldown(selectedYield.cooldownPeriod) && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Cooldown</span>
                  <span className="text-white">
                    {formatCooldown(selectedYield.cooldownPeriod)}
                  </span>
                </div>
              )}
              {selectedYield.minEntry &&
                parseFloat(selectedYield.minEntry) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Min. Deposit</span>
                    <span className="text-white">
                      ${parseFloat(selectedYield.minEntry).toFixed(2)}
                    </span>
                  </div>
                )}
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-400">Amount (USD)</label>
                <div className="flex items-center gap-1.5 text-sm">
                  <span className="text-gray-500">Balance:</span>
                  {loadingBalance ? (
                    <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                  ) : (
                    <span className="text-white font-medium">
                      {parseFloat(walletBalance).toFixed(2)} USD
                    </span>
                  )}
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="1.00"
                  min="1"
                  step="0.01"
                  className={`w-full px-4 py-3 pr-16 bg-gray-800/50 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors ${
                    depositExceedsBalance
                      ? "border-red-500/50 focus:border-red-500"
                      : "border-gray-700/50 focus:border-[#61CE70]/50"
                  }`}
                />
                <button
                  onClick={handleMaxClick}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#61CE70]/15 text-[#61CE70] text-xs font-semibold rounded-lg hover:bg-[#61CE70]/25 transition-colors"
                >
                  MAX
                </button>
              </div>
              {depositExceedsBalance && (
                <p className="text-xs text-red-400">
                  Amount exceeds your wallet balance
                </p>
              )}
              {depositAmount !== "" &&
                parseFloat(depositAmount) > 0 &&
                parseFloat(depositAmount) < 1 && (
                  <p className="text-xs text-yellow-400">
                    Minimum deposit is $1.00
                  </p>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeDepositModal}
                className="flex-1 py-3 bg-gray-800/50 text-gray-300 font-medium rounded-xl hover:bg-gray-700/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeposit}
                disabled={
                  depositing ||
                  !depositAmount ||
                  parseFloat(depositAmount) < 1 ||
                  depositExceedsBalance
                }
                className="flex-1 py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {depositing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ArrowDownToLine className="w-4 h-4" />
                    Deposit
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Your deposit will be processed through your CashTap wallet.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
